import React, { useState } from 'react';
import { FiChevronRight, FiChevronLeft } from "react-icons/fi";
import { FaBook } from "react-icons/fa";
import styled from 'styled-components';
import HashSimulator from '../components/P2P/HashSimulator';
import BlockSimulator from '../components/P2P/BlockchainSimulation';
import BlockSimulatorNetwork from '../components/P2P/BlockchainNetwork';
import TxChainSimulator from '../components/P2P/TxChainSimulator';
import GasPriorityChain from '../components/P2P/GasPriorityChain';
import MerkleMempoolSimulation from '../components/P2P/MerkleMempoolSimulation';
import Billeteras from '../components/P2P/AddressValidator';

// COLORES ARMÓNICOS
const COLOR_BG = "#02396d";
const COLOR_ACCENT = "#0151a2";
const COLOR_WHITE = "#fff";
const COLOR_SHADOW = "rgba(40,80,120,0.10)";

const SidebarContainer = styled.div`
  position: fixed;
  top: 4.5vw;
  left: 0;
  height: 100vh;
  width: ${(props) => (props.isCollapsed ? '80px' : '260px')};
  background: ${COLOR_BG};
  color: ${COLOR_WHITE};
  transition: width 0.3s;
  display: flex;
  flex-direction: column;
  border-right: none;
  z-index: 1000;
  overflow: hidden;
  box-shadow: 0 8px 24px 0 ${COLOR_SHADOW};

  @media (max-width: 768px) {
    top: 10vh;
    height: calc(100vh - 10vh);
  }
`;

const SidebarToggle = styled.div`
  position: absolute;
  right: -16px;
  top: 18px;
  background: transparent;
  color: ${COLOR_WHITE};
  border-radius: 50%;
  cursor: pointer;
  transition: 0.3s;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  z-index: 1010;
`;

const SidebarHeader = styled.div`
  text-align: center;
  padding: 20px 0 16px 0;
  border-bottom: 1px solid ${COLOR_ACCENT};
`;

const SidebarLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  h3 {
    color: ${COLOR_WHITE};
    font-weight: bold;
    font-size: 22px;
    margin: 0;
    letter-spacing: 1.2px;
  }
`;

const SidebarMenu = styled.nav`
  flex-grow: 1;
  overflow-y: auto;
  padding: 10px 0;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  a {
    display: flex;
    align-items: center;
    text-decoration: none;
    color: ${COLOR_WHITE};
    padding: 10px 18px;
    font-size: 16px;
    transition: background 0.2s, color 0.2s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: 8px;
  }
`;

const MenuItem = styled.li`
  a {
    background: ${(props) => (props.active ? COLOR_ACCENT : 'transparent')};
    font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
    color: ${COLOR_WHITE};
    box-shadow: ${(props) => (props.active ? '0 2px 12px 0 rgba(1,81,162,0.10)' : 'none')};
    &:hover {
      background: ${COLOR_ACCENT};
      color: ${COLOR_WHITE};
    }
  }
`;

const MainContent = styled.main`
  margin-left: ${(props) => (props.isSidebarCollapsed ? '80px' : '260px')};
  padding: 20px;
  box-sizing: border-box;
  transition: margin-left 0.3s;
`;

const MainComponent = () => {
  const [selectedOption, setSelectedOption] = useState('hash');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Elementos del menú lateral
  const menuItems = [
    { label: 'Hash', key: 'hash' },
    { label: 'Bloque', key: 'block' },
    { label: 'Red de Blockchain', key: 'net' },
    { label: 'Transacción', key: 'tx' },
    { label: 'Gas', key: 'gas' },
    { label: 'Merkle Tree', key: 'merkle' },
    { label: 'Billeteras', key: 'address' },
  ];

  const handleOptionSelect = (optionKey) => {
    setSelectedOption(optionKey);
  };

  const handleToggleSidebar = (collapsed) => {
    setIsSidebarCollapsed(collapsed);
  };

  const renderSidebar = () => (
    <SidebarContainer isCollapsed={isSidebarCollapsed}>
      <SidebarToggle onClick={() => handleToggleSidebar(!isSidebarCollapsed)}>
        {isSidebarCollapsed ? <FiChevronRight size={24} /> : <FiChevronLeft size={24} />}
      </SidebarToggle>

      <SidebarHeader>
        <SidebarLogo>
          <FaBook size={30} color={COLOR_WHITE} />
          {!isSidebarCollapsed && <h3>Simulador</h3>}
        </SidebarLogo>
      </SidebarHeader>

      <SidebarMenu>
        <ul>
          {menuItems.map((item) => (
            <MenuItem key={item.key} active={selectedOption === item.key}>
              <a href="#" onClick={() => handleOptionSelect(item.key)}>
                {isSidebarCollapsed ? item.label.charAt(0) : item.label}
              </a>
            </MenuItem>
          ))}
        </ul>
      </SidebarMenu>
    </SidebarContainer>
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      {renderSidebar()}
      <MainContent isSidebarCollapsed={isSidebarCollapsed}>
        {selectedOption === 'hash' && <HashSimulator />}
        {selectedOption === 'block' && <BlockSimulator />}
        {selectedOption === 'net' && <BlockSimulatorNetwork />}
        {selectedOption === 'tx' && <TxChainSimulator />}
        {selectedOption === 'gas' && <GasPriorityChain />}
        {selectedOption === 'merkle' && <MerkleMempoolSimulation />}
        {selectedOption === 'address' && <Billeteras />}
      </MainContent>
    </div>
  );
};

export default MainComponent;
